/**
 * Token 成本估算（与 bymy_ai/infrastructure/billing/token_pricing.py 价目表对齐）。
 * 历史记录 costCents=0 时，管理端列表用此函数回填展示。
 */

const TOKEN_CNY_PER_1M = {
  'deepseek-chat': [4, 12],
  'deepseek-reasoner': [4, 16],
  'deepseek-v4-flash': [1, 2],
  'deepseek-v4-pro': [12, 24],
  'qwen-turbo': [0.3, 0.6],
  'qwen-turbo-latest': [0.3, 0.6],
  'qwen-plus': [0.8, 2],
  'qwen-plus-latest': [0.8, 2],
  'qwen-max': [2.4, 9.6],
  'qwen-max-latest': [2.4, 9.6],
  'qwen-long': [0.5, 2],
  'qwen-vl-plus': [1.5, 4.5],
  'qwen-vl-max': [3, 9],
  'qwen3-vl-plus': [1.5, 4.5],
  'qwen3-vl-flash': [0.3, 0.9],
  'qwen-vl-ocr': [0.3, 0.9],
  'qwen-vl-ocr-latest': [0.3, 0.9],
  'glm-4-flash': [0.1, 0.1],
  'glm-4-plus': [0.5, 0.5],
  'glm-4-air': [0.1, 0.1],
  'doubao-1-5-pro-32k': [0.8, 2],
  'doubao-1-5-lite-32k': [0.3, 0.6],
  'doubao-1-5-vision-pro-32k': [1.5, 4.5]
}

const PROVIDER_DEFAULT_CNY = {
  deepseek: [4, 12],
  dashscope: [0.8, 2],
  zhipu: [0.5, 0.5],
  glm: [0.5, 0.5],
  ark: [0.8, 2]
}

function normalizeModel(model = '') {
  let text = String(model || '').trim().toLowerCase().replace(/_/g, '-')
  text = text.replace(/-20\d{2}-\d{2}-\d{2}$/, '')
  text = text.replace(/-\d{6,8}$/, '')
  return text
}

function normalizeProvider(provider = '') {
  return String(provider || '').trim().toLowerCase()
}

function lookupRates(model, provider) {
  const modelKey = normalizeModel(model)
  const providerKey = normalizeProvider(provider)
  if (TOKEN_CNY_PER_1M[modelKey]) {
    return { rates: TOKEN_CNY_PER_1M[modelKey], source: `official:${modelKey}` }
  }
  const prefix = Object.keys(TOKEN_CNY_PER_1M)
    .filter((key) => modelKey === key || modelKey.startsWith(`${key}-`))
    .sort((a, b) => b.length - a.length)[0]
  if (prefix) {
    return { rates: TOKEN_CNY_PER_1M[prefix], source: `official:${prefix}` }
  }
  if (PROVIDER_DEFAULT_CNY[providerKey]) {
    return { rates: PROVIDER_DEFAULT_CNY[providerKey], source: `official:provider:${providerKey}` }
  }
  return { rates: [0, 0], source: 'unknown' }
}

export function quoteTokenCostCents({
  provider = '',
  model = '',
  promptTokens = 0,
  completionTokens = 0
} = {}) {
  const prompt = Math.max(0, Number(promptTokens) || 0)
  const completion = Math.max(0, Number(completionTokens) || 0)
  const { rates, source } = lookupRates(model, provider)
  const [inCny, outCny] = rates
  const yuan = (prompt * inCny + completion * outCny) / 1_000_000
  return {
    costCents: Math.round(yuan * 100),
    inputCnyPer1M: inCny,
    outputCnyPer1M: outCny,
    source
  }
}

export function resolveRowCostCents(row) {
  const stored = Number(row?.costCents || 0)
  if (stored > 0) return stored
  if (String(row?.billingMode || '') !== 'token') return stored
  const prompt = Number(row?.promptTokens || 0)
  const completion = Number(row?.completionTokens || 0)
  if (prompt + completion <= 0) return 0
  return quoteTokenCostCents({
    provider: row?.provider,
    model: row?.model,
    promptTokens: prompt,
    completionTokens: completion
  }).costCents
}

export function tokenPricingHint(row) {
  const extra = typeof row?.extraJson === 'string'
    ? (() => { try { return JSON.parse(row.extraJson) } catch { return null } })()
    : row?.extraJson
  const pricing = extra?.pricing
  if (pricing?.rule) return pricing.rule
  if (String(row?.billingMode || '') !== 'token') return ''
  const quote = quoteTokenCostCents({
    provider: row?.provider,
    model: row?.model,
    promptTokens: row?.promptTokens,
    completionTokens: row?.completionTokens
  })
  if (!quote.inputCnyPer1M && !quote.outputCnyPer1M) return ''
  return `输入 ${quote.inputCnyPer1M}¥/M + 输出 ${quote.outputCnyPer1M}¥/M`
}
